import { IDocument, IProjectDocument } from "../../../../core/_models";
import { IInstitutionalSponsor, IObjectivesOutcomesIndicators, StepTab } from "../../ProjectInitiation-steps/core/_models";

// Field names match the stage 1 project_scope project-fields section.
export interface ILocationSpatialDefinition {
    // One province_id (0 when none); the section stores it as a one-item list.
    provinces: number;
    is_land_identified: string;
    is_land_ownership_confirmed: string;
    is_zoning_compliant: string;
}
export interface ISectorInfrastructureClassification {
    // infrastructure_sector lookup data_key ("" when none).
    infrastructure_sector: string;
    delivery_model_initial: string[];
}

export interface IDurationLifecyclePhase {
    planning_start_date: string | Date | null;
    completion_end_date: string | Date | null;
    construction_duration_years: number;
    construction_duration_months: number;
    operations_duration_years: number;
    operations_duration_months: number;
}

// What the project includes and explicitly excludes (rich text).
export interface IProjectScopeBoundaries {
    project_scope_in_scope: string;
    project_scope_out_of_scope: string;
}

export interface IOutputandServiceDeliveryDefination {
    infrastructure_type: string[];
    // A remark per selected infrastructure type.
    infrastructure_type_remarks: Record<string, string>;
    infrastructure_output_type: string;
    service_level: string;
    capacity: string;
}

export interface IInstitutionalArrangementsGovernance {
    // Saved through the section-items API (Stage 1), not with the project scope section.
    institutional_sponsors: IInstitutionalSponsor[];
    stakeholder_coordination_plan: string;
    // Stage 1 project documents (MoA / MoU / SLA); new ones (doc_id 0) upload when the step saves.
    signed_agreements: IProjectDocument[];
}

export interface IStrategicAlignmentPolicyIntegration {
    strategic_alignment_frameworks: string[];
    programme_alignment: string;
    related_projects: string[];
}

export interface IObjectiveArea {
    keyFocus: string[];
    objective: string;
    row_id: string | null;
    objective_name: string;
    keyFocus_name: string[];
    delete?: string;
}

export interface IPhase {
    phase_id: number | null;
    phase_name: string;
    timeline: string;
    key_activities: string;
    deliverables: string;
    budget_estimated: string;
}

export interface IBFIFiscalRequestJustification {
    fiscal_request_bfi: '' | 'yes' | 'no';
    // Required only when fiscal_request_bfi is yes.
    fiscal_request_reason: string;
}

export interface IPreliminaryCostRange {
    estimated_cost_min: number;
    estimated_cost_max: number;
    basis_of_estimate: string[];
}

export interface IProjectScope
    extends
    ILocationSpatialDefinition,
    ISectorInfrastructureClassification,
    IDurationLifecyclePhase,
    IProjectScopeBoundaries,
    IOutputandServiceDeliveryDefination,
    IInstitutionalArrangementsGovernance,
    IStrategicAlignmentPolicyIntegration,
    IBFIFiscalRequestJustification,
    IPreliminaryCostRange {

    formAction?: string;
    iscompleted?: boolean;
}

// Stage 1 objectives, outcomes and SMART indicators, each saved from its own popup.
export interface IObjectives extends IObjectivesOutcomesIndicators {
    formAction?: string;
    iscompleted?: boolean;
}

// Field names match the stage 1 strategic_significance_impact_framework project-fields section.
// Lists hold lookup data_keys; field names shared with Stage 0 keep the Stage 0 names.
export interface IStrategicSignificanceImpactFramework {
    // Strategic Infrastructure Rationale
    strategic_infrastructure_rationale: string;
    alignment_with_national_infrastructure_priorities: string;
    contribution_to_economic_development: string;
    long_term_strategic_significance: string;
    contribution_to_regional_national_development: string;
    // Why â€” Strategic Justification
    national_provincial_strategy_alignment: string[];
    alignment_evidence: string;
    mandate_delivery: string;
    legal_mandates_obligations: string[];
    national_alignment: string;
    urgent_challenges: string[];
    challenge_context: string;
    // What Change â€” Expected Impact
    service_delivery_improvements: string[];
    service_improvement_metrics: string;
    institutional_systemic_changes: string[];
    transformation_plan: string;
    economic_impact_areas: string[];
    job_creation_estimate: string;
    evidence_data_sources: string[];
    // Provincial Strategic Plan
    psp_strategic_objectives: string;
    growth_and_jobs_agenda: string;
    ndp_alignment: string;
    smme_strategy_alignment: string;
    sez_economic_zone_priorities: string;
    formAction?: string;
    iscompleted?: boolean;
}

// The parent section is read in one call; each tab saves to its own sub-section.
export const STRATEGIC_SIGNIFICANCE_SECTION = 'strategic_significance_impact_framework';

export const STRATEGIC_SIGNIFICANCE_TABS = {
  rationale: 1,
  strategic_justification: 2,
  expected_impact: 3,
  provincial_strategic_plan: 4,
} as const;

// Read in one call; each tab saves to the sub-section of the same name as its field.
export const SECTOR_SPECIFIC_ANALYSIS_SECTION = 'sector_specific_analysis';

export const SECTOR_ANALYSIS_TABS = {
  sector_trends: 1,
  institutional_context: 2,
  capacity_constraints: 3,
  regulatory_environment: 4,
  supporting_documents: 5,
} as const;

// The step key stays market_size_and_demand_analysis; its project-fields section is named differently.
export const MARKET_SIZE_DEMAND_ANALYSIS_SECTION = 'market_size_demand_analysis';

export const MARKET_ANALYSIS_TABS = {
  market_size: 1,
  target_market: 2,
  market_growth_and_gap: 3,
  market_assumptions: 4,
  supporting_documents: 5,
} as const;

// The step key stays strategic_futures_and_resilience_testing; its project-fields section is named
// differently. Supporting Documents are not section fields: they upload through the step's own API.
export const STRATEGIC_FUTURES_SECTION = 'strategic_futures_resilience_testing';

export const STRATEGIC_FUTURES_TABS = {
  scenario_analysis: 1,
  risk_mitigation: 2,
  documents: 3,
} as const;

// Objectives step tabs; every row saves from its own popup.
export const OBJECTIVES_TABS = {
  project_objectives: 1,
  project_outcomes: 2,
  smart_indicators: 3,
  supporting_documents: 4,
} as const;

// Sub-sections of the stage 1 justification project-fields section.
export const JUSTIFICATION_TABS = {
  needs_analysis: 1,
  root_causes: 2,
  demand_analysis: 3,
  supporting_documents: 4,
} as const;

// Benefit categories (lookup benefit_categories); each is a tab of the Benefits And Desired Outcomes
// step, with its subtitle and guidance in extra_data. The tabs and their ids are fixed here for the
// step's Save & Next; the shown title, subtitle and guidance come from the lookup, falling back to these.
export interface IBenefitCategory {
  data_key: string;
  display_name: string;
  data_value: number;
  extra_data: { description: string; guidance: string };
}

export const BENEFIT_CATEGORIES: ReadonlyArray<IBenefitCategory> = [
  {
    data_key: 'benefit_categories_1',
    display_name: 'Outcome Benefits',
    data_value: 1,
    extra_data: {
      description: 'Social/Economic Outcomes',
      guidance: 'List and describe all expected outcome-level benefits. Focus on improvements in conditions, not just deliverables. Quantify where possible.',
    },
  },
  {
    data_key: 'benefit_categories_2',
    display_name: 'Efficiency Gains',
    data_value: 2,
    extra_data: {
      description: 'Time/Cost/Service Improvement',
      guidance: 'What efficiency improvements will the project deliver? Quantify time savings, cost reductions, or service delivery improvements.',
    },
  },
  {
    data_key: 'benefit_categories_3',
    display_name: 'Access Improvement',
    data_value: 3,
    extra_data: {
      description: 'Coverage Expansion',
      guidance: 'How will the project expand or improve service access? Who will gain new or improved access?',
    },
  },
  {
    data_key: 'benefit_categories_4',
    display_name: 'Equity Impact',
    data_value: 4,
    extra_data: {
      description: 'Inclusion Effects',
      guidance: 'What equity or inclusion benefits will the project deliver? How will it address spatial, social, or economic inequalities?',
    },
  },
];

// Benefit tabs use the category's data_value as their id; the documents tab follows the last one.
export const BENEFITS_SUPPORTING_DOCUMENTS_TAB = Math.max(...BENEFIT_CATEGORIES.map((category) => category.data_value)) + 1;

// Tab-wise steps, keyed by step, with every tab in display order.
export const STEP_TABS: Partial<Record<string, ReadonlyArray<StepTab>>> = {
  justification: [
    {
      tabId: JUSTIFICATION_TABS.needs_analysis,
      title: 'Needs Analysis',
      section: 'needs_analysis',
      fields: ['problem_statement', 'affected_population', 'affected_population_remarks', 'severity_level', 'consequences_of_no_action', 'existing_services_baseline', 'justification_quantified_status_quo'],
    },
    // No section: root causes save from their own popup.
    { tabId: JUSTIFICATION_TABS.root_causes, title: 'Root Causes', fields: [] },
    {
      tabId: JUSTIFICATION_TABS.demand_analysis,
      title: 'Demand Analysis',
      section: 'demand_analysis',
      fields: ['current_demand', 'future_demand_growth_rate', 'future_demand_growth_description', 'supply_demand_gap', 'forecast_horizon', 'demand_data_sources'],
    },
    // Documents upload from their own popup, so the tab has no section or fields.
    { tabId: JUSTIFICATION_TABS.supporting_documents, title: 'Supporting Documents', fields: [] },
  ],
  // No sections: each tab is a benefit category whose outcomes are assigned from its popup.
  benefits_and_desired_outcomes: [
    ...BENEFIT_CATEGORIES.map((category) => ({
      tabId: category.data_value,
      title: category.display_name,
      fields: [`benefit_outcomes.${category.data_key}`],
    })),
    // Documents upload from their own popup, so the tab has no section or fields.
    { tabId: BENEFITS_SUPPORTING_DOCUMENTS_TAB, title: 'Supporting Documents', fields: [] },
  ],
  // No sections: objectives, outcomes and SMART indicators save from their popups.
  objectives: [
    { tabId: OBJECTIVES_TABS.project_objectives, title: 'Project Objectives', fields: ['project_objectives_areas'] },
    { tabId: OBJECTIVES_TABS.project_outcomes, title: 'Project Outcomes', fields: ['project_outcomes'] },
    { tabId: OBJECTIVES_TABS.smart_indicators, title: 'SMART Indicators', fields: ['smart_indicators'] },
    { tabId: OBJECTIVES_TABS.supporting_documents, title: 'Supporting Documents', fields: [] },
  ],
  market_size_and_demand_analysis: [
    { tabId: MARKET_ANALYSIS_TABS.market_size, title: 'Market Size', section: 'market_size', fields: ['market_size'] },
    { tabId: MARKET_ANALYSIS_TABS.target_market, title: 'Target Market', section: 'target_market', fields: ['target_market'] },
    { tabId: MARKET_ANALYSIS_TABS.market_growth_and_gap, title: 'Market Growth & Gap', section: 'market_growth_and_gap', fields: ['market_growth_rate', 'market_gap'] },
    { tabId: MARKET_ANALYSIS_TABS.market_assumptions, title: 'Assumptions', section: 'market_assumptions', fields: ['market_assumptions'] },
    // Documents upload from their own popup, so the tab has no section or fields.
    { tabId: MARKET_ANALYSIS_TABS.supporting_documents, title: 'Supporting Documents', fields: [] },
  ],
  sector_specific_analysis: [
    { tabId: SECTOR_ANALYSIS_TABS.sector_trends, title: 'Sector Trends', section: 'sector_trends', fields: ['sector_trends'] },
    { tabId: SECTOR_ANALYSIS_TABS.institutional_context, title: 'Institutional Context', section: 'institutional_context', fields: ['institutional_context'] },
    { tabId: SECTOR_ANALYSIS_TABS.capacity_constraints, title: 'Capacity Constraints', section: 'capacity_constraints', fields: ['capacity_constraints'] },
    { tabId: SECTOR_ANALYSIS_TABS.regulatory_environment, title: 'Regulatory Environment', section: 'regulatory_environment', fields: ['regulatory_environment'] },
    // Documents upload from their own popup, so the tab has no section or fields.
    { tabId: SECTOR_ANALYSIS_TABS.supporting_documents, title: 'Supporting Documents', fields: [] },
  ],
  strategic_significance_impact_framework: [
    {
      tabId: STRATEGIC_SIGNIFICANCE_TABS.rationale,
      title: 'Strategic Infrastructure Rationale',
      section: 'bfi_strategic_infrastructure_rationale',
      fields: [
        'strategic_infrastructure_rationale',
        'alignment_with_national_infrastructure_priorities',
        'contribution_to_economic_development',
        'long_term_strategic_significance',
        'contribution_to_regional_national_development',
      ],
    },
    {
      tabId: STRATEGIC_SIGNIFICANCE_TABS.strategic_justification,
      title: 'Why â€” Strategic Justification',
      section: 'strategic_justification_why',
      fields: [
        'national_provincial_strategy_alignment',
        'alignment_evidence',
        'mandate_delivery',
        'legal_mandates_obligations',
        'national_alignment',
        'urgent_challenges',
        'challenge_context',
      ],
    },
    {
      tabId: STRATEGIC_SIGNIFICANCE_TABS.expected_impact,
      title: 'What Change â€” Expected Impact',
      section: 'expected_impact_what_change',
      fields: [
        'service_delivery_improvements',
        'service_improvement_metrics',
        'institutional_systemic_changes',
        'transformation_plan',
        'economic_impact_areas',
        'job_creation_estimate',
        'evidence_data_sources',
      ],
    },
    {
      tabId: STRATEGIC_SIGNIFICANCE_TABS.provincial_strategic_plan,
      title: 'Provincial Strategic Plan',
      section: 'psp_alignment',
      fields: [
        'psp_strategic_objectives',
        'growth_and_jobs_agenda',
        'ndp_alignment',
        'smme_strategy_alignment',
        'sez_economic_zone_priorities',
      ],
    },
  ],
  strategic_futures_and_resilience_testing: [
    {
      tabId: STRATEGIC_FUTURES_TABS.scenario_analysis,
      title: 'Scenario Analysis',
      section: 'scenario_analysis',
      fields: [
        'resilience_justification',
        'optimistic_benefits',
        'optimistic_costs',
        'optimistic_justification',
        'mostlikely_benefits',
        'mostlikely_costs',
        'mostlikely_justification',
        'pessimistic_benefits',
        'pessimistic_costs',
        'pessimistic_justification',
      ],
    },
    {
      tabId: STRATEGIC_FUTURES_TABS.risk_mitigation,
      title: 'Risk Mitigation',
      section: 'risk_mitigation',
      fields: ['risk_mitigation_prevent', 'risk_mitigation_avoid', 'risk_mitigation_transfer', 'risk_mitigation_accept'],
    },
    // No section: the step's Save uploads the new documents.
    { tabId: STRATEGIC_FUTURES_TABS.documents, title: 'Supporting Documents', fields: ['documents'] },
  ],
};

// Field names match the stage 1 supplementary_infrastructure_planning project-fields section.
export interface ISupplementaryInfrastructurePlanning {
    supplementary_infrastructure_requirements: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface ISectorSpecificAnalysis {
    sector_trends: string;
    institutional_context: string;
    capacity_constraints: string;
    regulatory_environment: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IMarketSizeAndDemandAnalysis {
    market_size: string;
    target_market: string;
    market_growth_rate: string;
    market_gap: string;
    market_assumptions: string
    formAction?: string;
    iscompleted?: boolean;
}


// A Stage 1 project outcome assigned to a benefit category, with the user's own sub categories.
export interface IBenefitOutcome {
    outcome_id: number;
    sub_categories: string[];
}

export interface IBenefitsAndDesiredOutcomes {
    // The outcomes assigned to each benefit category, keyed by its data_key; saved from the assign popup.
    benefit_outcomes: Record<string, IBenefitOutcome[]>;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IInvestmentNeedsPolicyIncentives {
    funding_requirements: string;
    economic_rationale: string;
    public_sector_intervention_justification: string;
    grants_incentives_support_programmes: string;
    expected_economic_social_returns: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IStrategicFitSummaryDashboard {
    alignment_with_organisational_priorities: string;
    alignment_with_sector_plan: string;
    alignment_with_development_strategies: string;
    strategic_contribution: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface ISocioEconomicContext {
    economic_context: string;
    economic_gis_datasets: string;
    social_context: string;
    social_gis_datasets: string;
    environmental_context: string;
    environmental_gis_datasets: string;
    infrastructure_baseline: string;
    infrastructure_gis_datasets: string;
    distribution_effects: string;
    distribution_gis_datasets: string;
    formAction?: string;
    iscompleted?: boolean;
}

// Field names match the stage 1 swot_analysis project-fields section; lists hold lookup data_keys.
export interface ISWOTAnalysis {
    swot_strengths: string[]
    swot_weaknesses: string[]
    swot_opportunities: string[]
    swot_threats: string[]
    swot_strategic_positioning_feasibility_link: string
    formAction?: string;
    iscompleted?: boolean;

}

// Field names match the stage 1 key_business_drivers_why project-fields section. Each selected
// driver's notes are saved as its free_text and come back in business_case_drivers_remarks.
export interface IKeyBusinessDrivers {
    business_case_drivers: string[];
    business_case_drivers_remarks: Record<string, string>;
    formAction?: string;
    iscompleted?: boolean;
}

// Field names match the justification project-fields section (Stage 1).
export interface INeedsAnalysis {
    problem_statement: string;
    // Beneficiary group keys with a remark per key, as on Stage 0 Beneficiary Groups.
    affected_population: string[];
    affected_population_remarks: Record<string, string>;
    severity_level: string;
    consequences_of_no_action: string;
    existing_services_baseline: string;
    justification_quantified_status_quo: string;
}

export interface IDemandAnalysis {
    current_demand: string;
    future_demand_growth_rate: string;
    future_demand_growth_description: string;
    supply_demand_gap: string;
    forecast_horizon: string;
    demand_data_sources: string[];
}

export interface IJustification extends INeedsAnalysis, IDemandAnalysis {
    formAction?: string;
    iscompleted?: boolean;
}

export interface IDocumentData {
    documents: IDocument[]
    formAction?: string;
    iscompleted?: boolean;
}

export interface RejectIf {
    outputOnlyObjectives: boolean;
    noDemandData: boolean;
    noAlignmentToNationalOrProvincialPlans: boolean;
    noStakeholdersOrSponsorsDefined: boolean;
    lessThan2BusinessDriversDefined: boolean;
}

export interface MustHave {
    hasDefinedProblems: boolean;
    hasAtLeastOneSmartObjective: boolean;
    hasClearSponsor: boolean;
    hasStrategicAlignment: boolean;
    hasBasicSWOT: boolean;
    hasDemandVsCapacityComparison: boolean;
    hasQuantifiedDemand: boolean;
    hasMinimumTechnicalOptions: boolean;
}

export interface GateAssessmentData {
    reject_if: RejectIf;
    must_have: MustHave;
}

export interface ITechnicalOption {
    row_id: number | null;
    technical_option: number;        // transient client-side key; backend can ignore it
    description: string;
    pros: string;
    cons: string;
}

export interface ITechnicalOptionsAnalysis {
    technical_preferred_option: string;
    technical_preferred_option_selected: number | ""
    formAction?: string;
    iscompleted?: boolean;
}

// Field names match the stage 1 strategic_futures_resilience_testing project-fields section.
export interface IStrategicFuturesAndResilienceTesting {
    // Resilience Testing
    resilience_justification: string;

    // Optimistic Case
    optimistic_benefits: string;
    optimistic_costs: string;
    optimistic_justification: string;

    // Most Likely Case (Sponsor Case)
    mostlikely_benefits: string;
    mostlikely_costs: string;
    mostlikely_justification: string;

    // Pessimistic Case
    pessimistic_benefits: string;
    pessimistic_costs: string;
    pessimistic_justification: string;

    // Risk Mitigation
    risk_mitigation_prevent: string;
    risk_mitigation_avoid: string;
    risk_mitigation_transfer: string;
    risk_mitigation_accept: string;

    //Document References
    documents: IProjectDocument[];

    formAction?: string;
    iscompleted?: boolean;
}

export type IInfrastructurePlanningTypes =
    IProjectScope
    | IJustification
    | IObjectives
    | ISupplementaryInfrastructurePlanning
    | IStrategicSignificanceImpactFramework
    | ISectorSpecificAnalysis
    | IMarketSizeAndDemandAnalysis
    | IBenefitsAndDesiredOutcomes
    | IInvestmentNeedsPolicyIncentives
    | IStrategicFitSummaryDashboard
    | ISocioEconomicContext
    | ISWOTAnalysis
    | IKeyBusinessDrivers
    | IDocumentData
    | ITechnicalOptionsAnalysis
    | IStrategicFuturesAndResilienceTesting
